import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DndContext } from '@dnd-kit/core';
import { FileExplorerProvider, useFileExplorerContext } from './FileExplorerContext';
import { FullLayout } from './layouts/FullLayout';
import { CompactLayout } from './layouts/CompactLayout';
import { TableView } from './views/TableView';
import { GridView } from './views/GridView';
import { ActionToolbar } from './modules/ActionToolbar';
import { SearchBar } from './modules/SearchBar';
import { BreadcrumbNavigation } from './modules/BreadcrumbNavigation';
import { FilePreviewSidebar } from './modules/PreviewSidebar';
import { filterFiles } from './utils/filterFiles';
import { useFileDragDrop } from '../../hooks/useFileDragDrop';
import { FileDragOverlay } from './dnd/FileDragOverlay';
import { useFilePermissions } from '../../../auth/hooks/usePermissions';
import { FileDropZone } from '../../upload';
import { hasAction } from './modules/InlineRename';
import { actionsFor } from './modules/FileActionsMenu';

const NO_IDS = [];

/**
 * FileExplorer - Unified file browsing component
 *
 * @param {Object} props
 * @param {Array} props.folders - List of folders to display
 * @param {Array} props.files - List of files to display
 * @param {string} props.currentFolderId - Current folder ID
 * @param {Array} props.breadcrumbs - Breadcrumb navigation items
 *
 * @param {string} props.viewMode - View mode: 'table' | 'grid'
 * @param {string} props.layout - Layout mode: 'full' | 'compact'
 * @param {boolean} props.categorizeByDate - Enable date categorization
 *
 * @param {string} props.selectionMode - 'none' | 'single' | 'multi' | 'compare'
 * @param {Array} props.selectedFiles - Initial selection
 * @param {Function} props.onSelectionChange - Selection change callback
 * @param {Array} props.excludedFileIds - File IDs to disable
 * @param {Array} props.autoSelectFileIds - Files to add to the selection once they appear (e.g. just uploaded)
 *
 * @param {boolean} props.enableSearch - Enable search bar
 * @param {boolean} props.enablePreview - Enable file preview sidebar
 * @param {boolean} props.enableViewToggle - Enable view mode toggle
 * @param {boolean} props.enableCompareMode - Enable compare mode
 * @param {boolean} props.enableBreadcrumbs - Enable breadcrumb navigation
 * @param {boolean} props.enableCreateFolder - Enable create folder button
 * @param {boolean} props.enableUploadFile - Enable upload button
 *
 * @param {Array|Function} props.fileActions - Actions in each file's "⋯" menu: keys of
 *   FileActionsMenu's ACTIONS, or { key, label }; or file => those, when they differ per
 *   file (a file without "move" can't be dragged, one without "move" or "delete" can't
 *   be ticked). Picking one calls onFileAction.
 * @param {Function} props.folderActions - folder => actions for that folder's "⋯" menu
 *   (empty for none). Picking one calls onFolderAction(key, folder).
 * @param {string} props.parentFolderName - Raw name of the open folder; marks the folders
 *   Orbit made in its module folders as linked (see getFolderLink)
 * @param {Function} props.onRename - (type: 'file' | 'folder', item, newName) => Promise<boolean>.
 *   Items with a "rename" action can then be renamed in place (double-click the name, or F2).
 * @param {string} props.deleteLabel - Label for the preview's delete button (e.g. "Fjern fra udstyr")
 * @param {boolean} props.checkable - Show checkboxes for acting on several files
 * @param {Array} props.checkedFileIds - Checked files (controlled)
 * @param {Function} props.onCheckedChange - Receives the checked file objects
 * @param {React.ReactNode} props.checkedActions - Buttons shown while files are checked
 *
 * @param {Function} props.onFolderNavigate - Folder click callback
 * @param {Function} props.onBreadcrumbClick - Breadcrumb click callback
 * @param {Function} props.onFileAction - File action callback (download, rename, move, delete, remove)
 * @param {Function} props.onFilesDropped - Files dropped from the desktop; makes the list a drop area
 * @param {string} props.dropLabel - What the drop overlay says
 * @param {Function} props.onRefresh - Refresh callback
 * @param {Function} props.onCreateFolder - Create folder callback
 * @param {Function} props.onUploadFile - Upload button callback
 * @param {Function} props.onChooseFile - Shows a "Vælg fra Filer" button next to upload
 *
 * @param {React.ReactNode} props.actions - Custom action buttons
 * @param {React.ReactNode} props.notice - Shown under the breadcrumbs, e.g. about the open folder
 * @param {React.ReactNode} props.emptyState - Shown instead of "This folder is empty"
 * @param {React.ReactNode} props.previewSidebar - Custom preview sidebar
 */
const FileExplorerInternal = ({
  folders = [],
  files = [],
  currentFolderId,
  breadcrumbs = [],

  // Display modes
  layout = 'full',
  categorizeByDate = false,

  // Selection
  excludedFileIds = NO_IDS,
  autoSelectFileIds = NO_IDS,
  onSelectionChange,

  // Features
  enableSearch = false,
  enablePreview = true,
  enableViewToggle = true,
  enableCompareMode = false,
  enableBreadcrumbs = false,
  enableCreateFolder = false,
  enableUploadFile = false,

  // Actions
  fileActions,
  folderActions,
  onFolderAction,
  onRename,
  parentFolderName,
  deleteLabel,
  checkable = false,
  checkedFileIds = NO_IDS,
  onCheckedChange,
  checkedActions,

  // Callbacks
  onFolderNavigate,
  onBreadcrumbClick,
  onFileAction,
  onFilesDropped,
  dropLabel,
  onRefresh,
  onCreateFolder,
  onUploadFile,
  onChooseFile,

  // Customization
  actions,
  notice,
  emptyState,
  previewSidebar,
}) => {
  const { t } = useTranslation(['files', 'common']);
  const {
    selectedFiles,
    setSelectedFiles,
    clearSelection,
    viewMode,
    searchQuery,
    setSearchQuery,
    compareMode,
    selectionMode,
  } = useFileExplorerContext();

  const { canUpload } = useFilePermissions();

  const { dndContextProps, getDragData, isOver, activeItem } = useFileDragDrop({
    onRefresh,
    onFolderOpen: (folderId) => {
      // Subfolder in current view
      const folder = folders.find((f) => f.folder_id === folderId);
      if (folder) { onFolderNavigate?.(folder); return; }
      // Ancestor folder in breadcrumbs (folderId null = root)
      const crumb = breadcrumbs.find((c) => c.id === folderId);
      if (crumb) onBreadcrumbClick?.(crumb);
    },
    canDrag: canUpload,
    // A file or folder whose menu can't move it can't be dragged either
    canDragFolder: folderActions ? (folder) => folderActions(folder).includes('move') : undefined,
    canDragFile: typeof fileActions === 'function' ? (file) => hasAction(fileActions(file), 'move') : undefined,
  });

  // Category expansion state (for date categorization)
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Initialize all categories as expanded
  useEffect(() => {
    if (categorizeByDate && files.length > 0 && files[0]?.date) {
      const allDates = files.map(cat => cat.date);
      setExpandedCategories(new Set(allDates));
    }
  }, [files, categorizeByDate]);

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedFiles);
    }
  }, [selectedFiles, onSelectionChange]);

  // Clear selection on folder change (except in multi-select mode)
  useEffect(() => {
    if (!compareMode && selectionMode !== 'multi') {
      clearSelection();
    }
  }, [currentFolderId, compareMode, selectionMode, clearSelection]);

  const flatFiles = useMemo(() => (files.length > 0 && files[0]?.date
    ? files.flatMap(category => category.attachments || [])
    : files), [files]);

  // Selected files follow the list: one that's gone (deleted, moved) leaves the
  // preview, a renamed one shows its new name. Multi-select keeps picks from other
  // folders, so it's left alone.
  useEffect(() => {
    if (selectionMode === 'multi' || !selectedFiles.length) return;
    const listed = new Map(flatFiles.map(f => [f.file_id, f]));
    const changed = selectedFiles.some(f => listed.get(f?.file_id)?.filename !== f?.filename);
    if (changed) {
      setSelectedFiles(prev => prev.map(f => listed.get(f?.file_id)).filter(Boolean));
    }
  }, [flatFiles, selectedFiles, selectionMode, setSelectedFiles]);

  // Select files the parent asked for as soon as they're listed (e.g. just uploaded)
  useEffect(() => {
    if (!autoSelectFileIds.length) return;
    const chosen = new Set(selectedFiles.map(f => f?.file_id));
    const toAdd = flatFiles.filter(f => autoSelectFileIds.includes(f.file_id) && !chosen.has(f.file_id));
    if (!toAdd.length) return;
    setSelectedFiles(prev => (selectionMode === 'multi' ? [...prev, ...toAdd] : [toAdd[toAdd.length - 1]]));
  }, [autoSelectFileIds, flatFiles, selectedFiles, selectionMode, setSelectedFiles]);

  // Filter files based on search query
  const filteredFiles = enableSearch ? filterFiles(files, searchQuery) : files;

  // Handle category expansion
  const handleExpandCategory = (date) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  // Checkboxes (acting on several files)
  const checkedFiles = flatFiles.filter(f => checkedFileIds.includes(f.file_id));
  const toggleChecked = (file) => {
    onCheckedChange?.(checkedFileIds.includes(file.file_id)
      ? checkedFiles.filter(f => f.file_id !== file.file_id)
      : [...checkedFiles, file]);
  };

  // Choose layout
  const LayoutComponent = layout === 'full' ? FullLayout : CompactLayout;

  // Choose view component
  const ViewComponent = viewMode === 'grid' ? GridView : TableView;

  // The preview's delete button only where the file's own menu offers it
  const canDeleteFromPreview = (file) => {
    const actions = actionsFor(fileActions, file);
    return Boolean(onFileAction) && (hasAction(actions, 'delete') || hasAction(actions, 'remove'));
  };

  const sidebarFor = (file, side) => (
    <FilePreviewSidebar
      file={file}
      compareMode={compareMode}
      onDelete={canDeleteFromPreview(file) ? () => onFileAction('delete', file) : undefined}
      deleteLabel={deleteLabel}
      onRename={onRefresh}
      side={side}
    />
  );

  // Build preview sidebar
  const builtPreviewSidebar = (() => {
    if (!enablePreview) return null;

    // If custom previewSidebar is provided, use it
    if (previewSidebar) return previewSidebar;

    // Otherwise, build the sidebar based on selection
    const primaryFile = selectedFiles[0];
    const secondaryFile = selectedFiles[1];

    if (!primaryFile) return null;

    if (primaryFile && secondaryFile) {
      // Compare mode - show both files side by side
      return (
        <Box sx={{ display: 'flex', height: '100%' }}>
          <Box sx={{ flex: 1, borderRight: 1, borderColor: 'divider' }}>{sidebarFor(primaryFile, 'right')}</Box>
          <Box sx={{ flex: 1 }}>{sidebarFor(secondaryFile, 'left')}</Box>
        </Box>
      );
    }

    // Single file selected
    return sidebarFor(primaryFile, 'center');
  })();

  const view = (
    <ViewComponent
      folders={folders}
      files={filteredFiles}
      categorizeByDate={categorizeByDate}
      expandedCategories={expandedCategories}
      onExpandCategory={handleExpandCategory}
      onFolderClick={onFolderNavigate}
      onFileClick={() => {}} // Handled by context
      onFileAction={onFileAction}
      fileActions={fileActions}
      folderActions={folderActions}
      onFolderAction={onFolderAction}
      onRename={onRename}
      parentFolderName={parentFolderName}
      excludedFileIds={excludedFileIds}
      getDragData={getDragData}
      isOver={isOver}
      canDrop={canUpload}
      checkable={checkable}
      checkedIds={checkedFileIds}
      onToggleCheck={toggleChecked}
      onToggleCheckAll={(list) => onCheckedChange?.(list)}
      emptyState={emptyState}
    />
  );

  return (
    <DndContext {...dndContextProps}>
      <LayoutComponent previewSidebar={builtPreviewSidebar}>

        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Toolbar, or what to do with the checked files */}
          {checkedFiles.length > 0 ? (
            <Paper
              square
              elevation={0}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 1, pl: 2,
                borderBottom: 1, borderColor: 'divider', bgcolor: 'action.selected',
              }}
            >
              <Typography variant="body2" fontWeight={600} flex={1}>
                {t('files:checked_count', { count: checkedFiles.length })}
              </Typography>
              {checkedActions}
              <Button size="small" startIcon={<CloseIcon />} onClick={() => onCheckedChange?.([])}>
                {t('files:clear_checked')}
              </Button>
            </Paper>
          ) : (enableViewToggle || enableCompareMode || enableCreateFolder || enableUploadFile || onChooseFile || actions) && (
            <ActionToolbar
              enableViewToggle={enableViewToggle}
              enableCompareMode={enableCompareMode}
              enableCreateFolder={enableCreateFolder}
              enableUploadFile={enableUploadFile}
              onCreateFolder={onCreateFolder}
              onUploadFile={onUploadFile}
              onChooseFile={onChooseFile}
              actions={actions}
            />
          )}

          {/* Breadcrumbs */}
          {enableBreadcrumbs && breadcrumbs && breadcrumbs.length > 0 && (
            <Box sx={{ px: 2, pt: 2 }}>
              <BreadcrumbNavigation
                breadcrumbs={breadcrumbs}
                onBreadcrumbClick={onBreadcrumbClick}
                getDragData={getDragData}
                isOver={isOver}
                canDrop={canUpload}
              />
            </Box>
          )}

          {notice && <Box sx={{ px: 2, pt: 2 }}>{notice}</Box>}

          {/* Search Bar */}
          {enableSearch && (
            <Box sx={{ px: 2, pt: 2 }}>
              <SearchBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </Box>
          )}

          {/* File/Folder View; dropping files from the desktop onto it uploads them */}
          {onFilesDropped ? (
            <FileDropZone
              onFiles={onFilesDropped}
              label={dropLabel}
              sx={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              {view}
            </FileDropZone>
          ) : (
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{view}</Box>
          )}
        </Box>

        <FileDragOverlay activeItem={activeItem} />
      </LayoutComponent>
    </DndContext>
  );
};

/**
 * FileExplorer with context provider
 */
export default function FileExplorer(props) {
  const { viewMode: initialViewMode, selectionMode: initialSelectionMode, selectedFiles: initialSelectedFiles } = props;

  // Load saved view mode from localStorage
  let savedViewMode = 'table';
  try {
    savedViewMode = localStorage.getItem('fileExplorer_viewMode') || 'table';
  } catch (e) {
    console.warn('Failed to load view mode preference:', e);
  }

  const initialState = {
    viewMode: initialViewMode || savedViewMode,
    selectionMode: initialSelectionMode || 'single',
    selectedFiles: initialSelectedFiles || [],
  };

  return (
    <FileExplorerProvider initialState={initialState}>
      <FileExplorerInternal {...props} />
    </FileExplorerProvider>
  );
}
